/**
 * 📚 THE HOUSE LIBRARY, frontend half (HOUSE_LIBRARY_SPEC_2026_09_30.md §3,
 * §4.3 = this wave; the backend = justsomeguy-backend/src/ChatGame/houseBooks.js).
 *
 * Everything keys on `house.book` being PRESENT in the status payload: the key
 * is absent while the backend's HOUSE_LIBRARY_ENABLED is off (or the space is
 * not the house), and then the page is today's page, byte for byte.
 *
 * This module is pure (helpers + two cached fetchers + one POST that never
 * throws). The page (GameStepPage) holds ONE book state, fed by the status
 * poll, the `house_book_state` socket message and bookControl answers, and
 * keeps it only when `seq` is not older (pickNewerBook): a stale poll or an
 * out-of-order message never rewinds a page.
 *
 * ⚠️ A page turn goes ONLY through bookControl (POST /api {bookControl:true}),
 * never the interjection path: that one wakes the house (a round = 7-12 paid
 * AI turns).
 * ⚠️ Book text renders as TEXT NODES only (no HTML from a book, no iframe).
 * Player copy: short, punchy, no em dashes.
 */
import { API_BASE_URL } from '../config/api';
import { houseTodayKey, HOUSE_TZ_FALLBACK } from './houseMemoryBook';

export const LIBRARY_ROOM = 'library';
export const BOOK_STALE_MS = 60 * 1000;   // the TV's 60 s remote rule (houseBooks STALE_MS)
export const BOOK_ASK_TTL_MS = 12 * 1000; // the TV's request timeout (houseBooks ASK_TTL_MS)
export const SHELF_CHIP = '📚 Shelf';
const OPEN_CHIP_RE = /^📖 .+ · p\. \d+$/;

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const int = (v, d = 0) => { const n = Number(v); return Number.isFinite(n) ? Math.trunc(n) : d; };

// ── the state ───────────────────────────────────────────────────────────────
/** The payload's seq (0 = never opened; a bad value reads 0). */
export function bookSeq(b) {
  return isObj(b) ? Math.max(0, int(b.seq, 0)) : -1;
}

/** ⭐⭐ THE seq rule: keep `incoming` only when it is a real payload and its seq
 *  is not older than the one held. Equal seq is accepted (the same state can
 *  carry a fresher `now`, `takeable` or a derived close). */
export function pickNewerBook(held, incoming) {
  if (!isObj(incoming) || typeof incoming.state !== 'string') return held || null;
  if (!isObj(held)) return incoming;
  return bookSeq(incoming) >= bookSeq(held) ? incoming : held;
}

/** An open book (a derived close reads 'closed' too). */
export function isBookOpen(b) {
  return !!(isObj(b) && b.state === 'open' && typeof b.book === 'string' && b.book);
}

/** Two payloads that show the SAME thing: every field equal except `now` (the
 *  server clock moves on every poll; nothing on screen reads it directly). */
export function sameBookView(a, b) {
  if (!isObj(a) || !isObj(b)) return false;
  if (bookSeq(a) !== bookSeq(b)) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  keys.delete('now');
  for (const k of keys) {
    if (a[k] === b[k]) continue;
    if (JSON.stringify(a[k] === undefined ? null : a[k]) !== JSON.stringify(b[k] === undefined ? null : b[k])) return false;
  }
  return true;
}

/** After a STALE turn answer (the server's own state came back): the seq to turn
 *  again with, ONCE, or null. Only while that state is the same open book still
 *  on the page the reader turned FROM: someone else already moving it = never a
 *  double turn. A too_fast answer has its own wait-and-retry. */
export function staleTurnRetrySeq(res, slug, from) {
  if (!isObj(res) || res.stale !== true || res.too_fast || !isObj(res.book)) return null;
  const b = res.book;
  if (!isBookOpen(b) || b.book !== slug || int(b.page, -1) !== int(from, -2)) return null;
  return bookSeq(b);
}

/** The server clock the payload carried, moved forward by the local time since
 *  it arrived (the client's own skew; a missing `now` = the local clock). */
export function serverNowFor(b, receivedAt, localNow = Date.now()) {
  if (!isObj(b) || !Number.isFinite(Number(b.now)) || !Number.isFinite(Number(receivedAt))) return localNow;
  return Number(b.now) + (localNow - Number(receivedAt));
}

const holderSeat = (b) => (isObj(b) && isObj(b.holder) && b.holder.seat ? String(b.holder.seat) : null);
/** Do I hold the bookmark? */
export function isHolder(b, me) {
  return !!(me && isBookOpen(b) && holderSeat(b) === String(me));
}
/** Is the bookmark loose RIGHT NOW (no holder · the server said takeable · the
 *  60 s rule passed on the server's clock)? */
export function bookmarkLoose(b, serverNow) {
  if (!isBookOpen(b)) return false;
  if (!holderSeat(b) || b.takeable === true) return true;
  const at = Number(b.holder_stale_at);
  return Number.isFinite(at) && at > 0 && Number(serverNow) >= at;
}
/** May I turn the page? The holder, or anyone once the bookmark is loose (the turn takes it). */
export function canTurnPage(b, me, serverNow) {
  return !!me && (isHolder(b, me) || bookmarkLoose(b, serverNow));
}
/** May I take the bookmark (not mine, and loose)? A book nobody holds (book club
 *  opens it holderless) is loose: anyone in the library may take it or turn. */
export function canTakeBookmark(b, me, serverNow) {
  return !!me && isBookOpen(b) && !isHolder(b, me) && bookmarkLoose(b, serverNow);
}
/** May I ask for it (someone else holds it, fresh)? */
export function canAskBookmark(b, me, serverNow) {
  return !!me && isBookOpen(b) && !!holderSeat(b) && !isHolder(b, me) && !bookmarkLoose(b, serverNow);
}
/** The ask the HOLDER should answer, while it is fresh (≤ 12 s), else null. */
export function askForHolder(b, me, serverNow) {
  if (!isHolder(b, me) || !isObj(b.ask) || !b.ask.seat || String(b.ask.seat) === String(me)) return null;
  const at = Number(b.ask.at);
  if (!Number.isFinite(at) || Number(serverNow) - at > BOOK_ASK_TTL_MS) return null;
  return { seat: String(b.ask.seat), name: b.ask.name || 'Someone', at };
}

/** "Your bookmark" · "Nina has the bookmark" · "The bookmark is free". */
export function bookmarkWords(b, me, serverNow) {
  if (!isBookOpen(b)) return '';
  if (isHolder(b, me)) return 'Your bookmark';
  if (!holderSeat(b)) return 'The bookmark is free';
  const name = (b.holder && b.holder.name) || 'Someone';
  return bookmarkLoose(b, serverNow) ? `${name} left the bookmark` : `${name} has the bookmark`;
}
/** The book club's open (`house.book.club`, true while the club runs). */
export function isBookClub(b) {
  return isBookOpen(b) && b.club === true;
}
/** The Shelf's Reading now row: "Book club" while the club reads and nobody
 *  holds the bookmark, else the bookmark's own words. */
export function readingNowWords(b, me, serverNow) {
  if (isBookClub(b) && !holderSeat(b)) return 'Book club';
  return bookmarkWords(b, me, serverNow);
}

/** "p. 42 of 430" (or "p. 42" with no count). */
export function pageLine(page, pages) {
  const p = int(page, 0);
  if (p < 1) return '';
  const n = int(pages, 0);
  return n >= p ? `p. ${p} of ${n}` : `p. ${p}`;
}

/** The library's action chip: "📚 Shelf", or "📖 Frankenstein · p. 42" while a book is open. */
export function shelfChipLabel(b) {
  if (!isBookOpen(b)) return SHELF_CHIP;
  const title = String(b.title || 'A book').replace(/\s+/g, ' ').trim().slice(0, 40);
  return `📖 ${title} · p. ${Math.max(1, int(b.page, 1))}`;
}
/** Is this floor chip the library's chip (either face)? */
export function isShelfChip(v) {
  return typeof v === 'string' && (v === SHELF_CHIP || OPEN_CHIP_RE.test(v));
}
/** The library's verbs with the chip in front: it REPLACES /borrow (bare
 *  /borrow opens the shelf now), the rest keep their order. */
export function withShelfChip(verbs, b) {
  const rest = (Array.isArray(verbs) ? verbs : []).filter((v) => !/^\/borrow\b/.test(String(v || '').trim()));
  return [shelfChipLabel(b), ...rest];
}

// ── /borrow ────────────────────────────────────────────────────────────────
/** `/borrow` (bare) = the shelf; `/borrow frankenstein` = open that book (the server
 *  resolves a slug, a title or a unique prefix). Quotes and a trailing period go. */
export function parseBorrow(text) {
  const q = String(text == null ? '' : text).replace(/\s+/g, ' ').trim()
    .replace(/^["'“‘]+|["'”’.!?]+$/g, '').trim();
  return q ? { kind: 'open', query: q.slice(0, 120) } : { kind: 'shelf' };
}
/** An Odd shelf title typed after /borrow (exact, any case, "the" optional) → its entry. */
export function oddBookFor(query, manifest) {
  const odd = isObj(manifest) && Array.isArray(manifest.odd) ? manifest.odd : [];
  const bare = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/^(the|a|an) /, '');
  const q = bare(query);
  if (!q) return null;
  return odd.find((o) => o && o.title && bare(o.title) === q) || null;
}

// ── the manifest (shelf.json) + chapters ─────────────────────────────────────
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const FILE_RE = /^ch-\d{3,4}\.json$/;
const baseOf = (base) => (typeof base === 'string' && /^https?:\/\//.test(base.trim()) ? base.trim().replace(/\/+$/, '') : null);

export function shelfUrl(base) {
  const b = baseOf(base);
  return b ? `${b}/shelf.json` : null;
}
/** `${base_url}/${book}/${file}` (W0's layout), or null for anything odd. */
export function chapterUrl(base, slug, file) {
  const b = baseOf(base);
  if (!b || !SLUG_RE.test(String(slug || '')) || !FILE_RE.test(String(file || ''))) return null;
  return `${b}/${slug}/${file}`;
}

/** A manifest book by slug. */
export function shelfBook(manifest, slug) {
  if (!isObj(manifest) || !Array.isArray(manifest.books)) return null;
  return manifest.books.find((x) => x && x.slug === slug) || null;
}

/** The chapter that holds a page (the backend's chapterFor: a page in a gap
 *  reads as the chapter before it; before the first = the first; past the
 *  last = the last). */
export function chapterForPage(entry, page) {
  const chs = isObj(entry) && Array.isArray(entry.chapters) ? entry.chapters.filter((c) => isObj(c) && Number.isFinite(Number(c.first))) : [];
  if (!chs.length) return null;
  const sorted = [...chs].sort((x, y) => Number(x.first) - Number(y.first));
  const p = int(page, 1);
  const hit = sorted.find((c) => p >= Number(c.first) && p <= Number(c.last));
  if (hit) return hit;
  if (p < Number(sorted[0].first)) return sorted[0];
  let before = sorted[0];
  for (const c of sorted) { if (Number(c.first) <= p) before = c; }
  return before;
}
/** The chapter right after `chapter` (by first page), or null. */
export function chapterAfter(entry, chapter) {
  if (!isObj(entry) || !Array.isArray(entry.chapters) || !isObj(chapter)) return null;
  const sorted = entry.chapters.filter(isObj).sort((x, y) => Number(x.first) - Number(y.first));
  return sorted.find((c) => Number(c.first) > Number(chapter.last)) || null;
}
/** Prefetch the next chapter once the page is within `ahead` pages of this chapter's end. */
export function wantsNextChapter(page, chapter, ahead = 1) {
  return isObj(chapter) && int(page, 0) >= int(chapter.last, 0) - ahead;
}
/** A page's paragraphs from a chapter file, or null when the file does not hold it. */
export function parasForPage(chapterJson, page) {
  if (!isObj(chapterJson) || !Array.isArray(chapterJson.pages)) return null;
  const p = chapterJson.pages.find((x) => isObj(x) && int(x.n, -1) === int(page, -2));
  if (!p || !Array.isArray(p.paras)) return null;
  return p.paras.filter((s) => typeof s === 'string').map((s) => s.replace(/<\/?user_input>/gi, ''));
}

// Two warm caches, one promise per URL (a second caller shares the first
// fetch; a failure leaves the cache so the next look can try again).
// ⏱️ A request that hangs gives up at FETCH_TIMEOUT_MS (aborted) and leaves the
// cache too: a hung promise never sits there, so "Try again" really fetches again.
export const FETCH_TIMEOUT_MS = 8000;
const shelfCache = new Map();
const chapterCache = new Map();
function cachedJson(cache, url, fetchFn) {
  if (!url) return Promise.resolve(null);
  if (cache.has(url)) return cache.get(url);
  const f = fetchFn || (typeof fetch === 'function' ? fetch : null);
  if (!f) return Promise.resolve(null);
  const ctl = typeof AbortController === 'function' ? new AbortController() : null;
  let timer = null;
  const timedOut = new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      try { if (ctl) ctl.abort(); } catch (e) { /* already settled */ }
      reject(new Error('timeout'));
    }, FETCH_TIMEOUT_MS);
  });
  const load = Promise.resolve()
    .then(() => f(url, ctl ? { credentials: 'omit', signal: ctl.signal } : { credentials: 'omit' }))
    .then((r) => (r && r.ok ? r.json() : Promise.reject(new Error(`status ${r && r.status}`))))
    .then((j) => (isObj(j) ? j : Promise.reject(new Error('not an object'))));
  let p = null;
  p = Promise.race([load, timedOut]).then(
    (j) => { clearTimeout(timer); return j; },
    (e) => { clearTimeout(timer); if (cache.get(url) === p) cache.delete(url); throw e; },
  );
  cache.set(url, p);
  return p;
}
export function loadShelf(base, fetchFn) { return cachedJson(shelfCache, shelfUrl(base), fetchFn); }
export function loadChapter(base, slug, file, fetchFn) { return cachedJson(chapterCache, chapterUrl(base, slug, file), fetchFn); }
/** Already in hand (no network)? → the chapter JSON or null. */
export function peekChapter(base, slug, file) {
  const url = chapterUrl(base, slug, file);
  return url && chapterCache.has(url) ? chapterCache.get(url) : null;
}
export function _resetLibraryCachesForTest() { shelfCache.clear(); chapterCache.clear(); }

// ── bookControl ─────────────────────────────────────────────────────────────
/**
 * POST /api {bookControl:true, …}. NEVER throws, and READS THE BODY ON A 400
 * (the backend answers an in-fiction refusal as 400 {error, code, book}; the
 * watchParty helper throws that body away, this one must not).
 * → { ok, status, ...body } · a network failure → { ok:false, status:0, error }.
 */
export async function postBookControl({ gameId, playerId, action, params = {}, fetchFn, base = API_BASE_URL } = {}) {
  const f = fetchFn || (typeof fetch === 'function' ? fetch : null);
  if (!f || !gameId || !playerId || !action) return { ok: false, status: 0, error: 'Not seated here.' };
  let secret = null;
  try { secret = window.localStorage.getItem(`player_secret_${playerId}`); } catch (e) { /* private mode */ }
  try {
    const res = await f(`${base}/api`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bookControl: true, game_id: gameId, player_id: playerId, player_secret: secret, action, ...params }),
    });
    let body = {};
    try { body = await res.json(); } catch (e) { body = {}; }
    if (!isObj(body)) body = {};
    if (res.status === 429) return { ok: false, status: 429, ...body, error: body.error || 'Slow down a little.' };
    return { ...body, ok: !!res.ok && body.success !== false, status: res.status };
  } catch (e) {
    return { ok: false, status: 0, error: "The page didn't turn. Try again." };
  }
}

// ── people ──────────────────────────────────────────────────────────────────
/** "Fen is reading along" · "Fen and Ember are reading along" · "Fen, Ember and 2 more are reading along". */
export function readingAlongLine(names) {
  const list = (Array.isArray(names) ? names : []).filter((n) => typeof n === 'string' && n.trim()).map((n) => n.trim());
  if (!list.length) return '';
  if (list.length === 1) return `${list[0]} is reading along`;
  if (list.length === 2) return `${list[0]} and ${list[1]} are reading along`;
  return `${list[0]}, ${list[1]} and ${list.length - 2} more are reading along`;
}

// ── the join line (§3.5: once per book open per device) ─────────────────────
/** A per-device quiet time between two join lines: an open/close flap never re-pops it. */
export const JOIN_COOLDOWN_MS = 10 * 60 * 1000;
/** The key of one OPEN: the book and the moment it opened (`opened_at`), else
 *  (an older payload) the book and who opened it. A close the device sees
 *  forgets it, so the next open of the same book shows again. */
export function openKey(b) {
  if (!isBookOpen(b)) return null;
  const at = Number(b.opened_at);
  return Number.isFinite(at) && at > 0 ? `${b.book}|${Math.trunc(at)}` : `${b.book}|${b.opened_by_name || ''}`;
}
/**
 * One step of the once-per-open rule. `stored` = { key, seen, shown_at } from
 * this device (or null). → { show, store } where `store` is what to remember
 * (null = forget). Standing in the library counts as seen (you are there).
 * `shown_at` (local ms) = when this device last showed a join line: a NEW open
 * inside JOIN_COOLDOWN_MS of it waits (it is not marked seen, so it can still
 * show once the quiet time is over and the book changes).
 */
export function joinLineStep(stored, b, viewerRoom, localNow = Date.now()) {
  const s = isObj(stored) ? stored : null;
  const shownAt = s && Number.isFinite(Number(s.shown_at)) && Number(s.shown_at) > 0 ? Number(s.shown_at) : null;
  const keep = shownAt != null ? { shown_at: shownAt } : {};
  const key = openKey(b);
  // a close: forget the open, keep the quiet time
  if (!key) return { show: false, store: shownAt != null ? keep : null };
  const here = viewerRoom === LIBRARY_ROOM;
  if (s && s.key === key) {
    if (here) return { show: false, store: s.seen ? s : { ...s, key, seen: true } };
    return { show: !s.seen, store: s };
  }
  if (here) return { show: false, store: { ...keep, key, seen: true } };
  const now = Number(localNow);
  const quiet = shownAt != null && Number.isFinite(now) && now >= shownAt && now - shownAt < JOIN_COOLDOWN_MS;
  if (quiet) return { show: false, store: s };
  return { show: true, store: { key, seen: false, shown_at: Number.isFinite(now) ? now : Date.now() } };
}
/** "📖 Nina opened Frankenstein in the library" (who opened it, else who reads);
 *  while the book club runs: "📚 Book club: <the title> in the library". */
export function joinLineText(b) {
  if (!isBookOpen(b)) return '';
  if (isBookClub(b)) return `📚 Book club: ${b.title || 'a book'} in the library`;
  const who = b.opened_by_name || (b.holder && b.holder.name) || 'Someone';
  return `📖 ${who} opened ${b.title || 'a book'} in the library`;
}
export function readJoinStore(gameId) {
  try { const raw = window.localStorage.getItem(`library_join_${gameId}`); const v = raw ? JSON.parse(raw) : null; return isObj(v) ? v : null; } catch (e) { return null; }
}
export function writeJoinStore(gameId, v) {
  try {
    if (v) window.localStorage.setItem(`library_join_${gameId}`, JSON.stringify(v));
    else window.localStorage.removeItem(`library_join_${gameId}`);
  } catch (e) { /* private mode: the line just shows again next time */ }
}

// ── the shelf's look ────────────────────────────────────────────────────────
// Spine colors from the mockup, drawn by a hash of the slug (every viewer sees
// the same shelf; never a random).
const SPINE_COLORS = ['#5a1f24', '#2f3b33', '#6b4a1f', '#7a3b3b', '#4a3a5c', '#3f6b4a', '#8a5a2b', '#2e5d7a', '#556b2f', '#2f6b5c', '#3b3b3b', '#5c2f4a', '#7a6a2b', '#1f4a5c', '#6b4a5c', '#8a6b4a'];
function hash32(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
export function spineLook(slug) {
  const h = hash32(String(slug || 'book'));
  return { bg: SPINE_COLORS[h % SPINE_COLORS.length], h: 128 + ((h >>> 8) % 43) };
}
/** "STOKER" (the last word of the author's name). */
export function spineAuthor(author) {
  const parts = String(author || '').replace(/[.,]/g, ' ').trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts[parts.length - 1].toUpperCase() : '';
}

/** 🍂 Is it October on the house clock (America/Phoenix, no DST)? `now` = ms. */
export function isHouseOctober(now = Date.now()) {
  const t = Number(now);
  return houseTodayKey(HOUSE_TZ_FALLBACK, Number.isFinite(t) && t > 0 ? t : Date.now()).slice(5, 7) === '10';
}

/** The shelf rows from shelf.json: { october, classics, libby, odd, labels, credit }.
 *  🍂 The October row is drawn ONLY in October, house time (Nina 10-01). Any
 *  other month its books fold onto the END of the classics row (still readable,
 *  same count; the shelf never opens with three Halloween books in spring) and
 *  `october` is empty, so the row is not drawn.
 *  `now` = the clock to read (ms); the shelf passes the server's. */
export function shelfModel(manifest, opts) {
  const now = opts && opts.now != null ? opts.now : Date.now();
  const m = isObj(manifest) ? manifest : {};
  const books = (Array.isArray(m.books) ? m.books : []).filter((b) => isObj(b) && SLUG_RE.test(String(b.slug || '')));
  const rowOf = (b) => (Array.isArray(b.rows) && b.rows[0]) || 'classics';
  const rows = Array.isArray(m.rows) ? m.rows.filter(isObj) : [];
  const labelOf = (id, d) => { const r = rows.find((x) => x.id === id); return { label: (r && r.label) || d.label, sub: (r && r.sub) || d.sub || '' }; };
  const october = isHouseOctober(now);
  const onOctoberRow = (b) => october && rowOf(b) === 'october';
  return {
    october: books.filter(onOctoberRow),
    classics: [...books.filter((b) => rowOf(b) !== 'october'), ...books.filter((b) => !october && rowOf(b) === 'october')],
    libby: (Array.isArray(m.libby) ? m.libby : []).filter((x) => isObj(x) && x.title && /^https:\/\/share\.libbyapp\.com\/title\/\d+\/?$/.test(String(x.share_url || ''))),
    odd: (Array.isArray(m.odd) ? m.odd : []).filter((x) => isObj(x) && x.title && x.page),
    labels: {
      october: labelOf('october', { label: '🍂 October', sub: 'for the season' }),
      classics: labelOf('classics', { label: 'The shelf', sub: '' }),
      libby: labelOf('libby', { label: 'Newer books', sub: '' }),
      odd: labelOf('odd', { label: 'Odd shelf', sub: 'Nobody knows who wrote these' }),
    },
    credit: typeof m.credit === 'string' ? m.credit : '',
  };
}
/** A Libby link we may open (https share.libbyapp.com only), else null. */
export function libbyHref(url) {
  return /^https:\/\/share\.libbyapp\.com\/title\/\d+\/?$/.test(String(url || '')) ? String(url) : null;
}

// ── the phone bubbles (the scene chat's shape: { key, name, ai, text, emote, at }) ──
const CHAT_TYPES = new Set(['public_message', 'human_interjection']);
/** The newest `max` public lines of the room, oldest first. `seatOf(id)` →
 *  { name, ai } | null (the page's roster). Optimistic rows are replaced by
 *  their real row under the same key (client_msg_id). */
export function bookChatLines(actions, seatOf = null, max = 4) {
  const byKey = new Map();
  for (const a of Array.isArray(actions) ? actions : []) {
    if (!a || !CHAT_TYPES.has(a.type)) continue;
    const d = a.data || {};
    const text = typeof d.message === 'string' ? d.message.trim() : '';
    if (!text) continue;
    const at = Date.parse(a.created_at) || 0;
    const key = String(d.client_msg_id || a.id || `${a.game_instance_player_id || ''}|${at}`);
    const was = byKey.get(key);
    if (was && !was.optimistic) continue;
    const seat = (typeof seatOf === 'function' && a.game_instance_player_id) ? seatOf(a.game_instance_player_id) : null;
    const name = (a.type === 'human_interjection' ? (d.sender_name || (seat && seat.name)) : ((seat && seat.name) || d.sender_name)) || 'Someone';
    const ai = (seat && typeof seat.ai === 'boolean') ? seat.ai : a.type === 'public_message';
    byKey.set(key, { key, name: String(name), ai, text, emote: d.is_emote === true, at, optimistic: !!a.isOptimistic });
  }
  return [...byKey.values()].sort((p, q) => p.at - q.at).slice(-Math.max(1, max)).map(({ optimistic, ...l }) => l);
}
